import { FormArray, FormControl, FormGroup } from "@angular/forms";
import { BmItemForm, BmPeriodForm } from "../../pojo/bm-form";
import { BmCalcType } from "../../pojo/bm-enums";
import { BmItem, BmPeriodEnum } from "../../pojo/bm-classes";

export class BmCalcHelperService {
  /**
   * Groups subGroupData items by yearNumber.
   *
   * Example: [Q1-1,Q2-1,Q3-1,Q4-1,Y1,   Q1-2,Q2-2,Q3-2,Q4-2,Y2]
   * return  1 : {Y1, [Q1-1,Q2-1,Q3-1,Q4-1]}
   *         2 : {Y2, [Q1-2,Q2-2,Q3-2,Q4-2]}
   */
  public static groupItemsByYear(
    items: FormArray<FormGroup<BmItemForm>>
  ): Map<number, { yearFG: FormGroup<BmItemForm>; quartersFG: FormGroup<BmItemForm>[] }> {
    const map = new Map<number, { yearFG: FormGroup<BmItemForm>; quartersFG: FormGroup<BmItemForm>[] }>();
    items.controls.forEach((item): void => {
      const period = item.value.period;
      // set Year Number
      if (!map.has(period.yearNumber)) {
        map.set(period.yearNumber, { yearFG: undefined, quartersFG: [] });
      }
      // set Year Item and Quarter Items Array
      const mapItem = map.get(period.yearNumber);
      switch (period.type) {
        case BmPeriodEnum.YEAR:
          mapItem.yearFG = item;
          break;
        case BmPeriodEnum.QUARTER:
          mapItem.quartersFG.push(item);
          break;
      }
    });
    return map;
  }

  /**
   * Disable all values in FormArray.
   * If it had MANUAL value => return
   *
   * @param items selected FormArray
   */
  public static disableItems(items: FormArray<FormGroup<BmItemForm>>): void {
    items.controls.forEach((item) => {
      if (item.value.calcType !== BmCalcType.MANUAL) {
        item.controls.itemValue.disable({ emitEvent: false });
      }
    });
  }

  /**
   * Return array of quarters in selected year in selected subGroup array
   *
   * @param formArray selected FormArray
   * @param yearNumber selected year number
   */
  public static findQuarterItemsInSelectedYear(formArray: FormArray<FormGroup<BmItemForm>>, yearNumber: number): FormGroup<BmItemForm>[] {
    return formArray.controls.filter((quarter: FormGroup<BmItemForm>) =>
      quarter.value.period?.type === BmPeriodEnum.QUARTER && quarter.value.period?.yearNumber === yearNumber
    );
  }

  /**
   * Return selected year in selected subGroup array
   *
   * @param formArray selected FormArray
   * @param yearNumber selected year number
   */
  public static findYearItemInSelectedYear(formArray: FormArray<FormGroup<BmItemForm>>, yearNumber: number): FormGroup<BmItemForm> {
    return formArray.controls.find((c) =>
      c.value.period.type === BmPeriodEnum.YEAR && c.value.period.yearNumber === yearNumber);
  }

  /**
   * Return `true` when item represents a YEAR-type period.
   * */
  public static isYear(item: FormGroup<BmItemForm>): boolean {
    return item.controls.period.value.type === BmPeriodEnum.YEAR;
  }

  /**
   * Find Item by Year and Quarter number from selected array
   */
  public static findItem(formArray: FormArray<FormGroup<BmItemForm>>, quarterNum: number, yearNum: number): FormGroup<BmItemForm> {
    return formArray.controls.find((itemFg) => {
      const period = itemFg.controls.period.controls;

      return (
        period.quarterNumber.value === quarterNum &&
        period.yearNumber.value === yearNum
      );
    });
  }

  /**
   * Calculate sum of all quarter values in selected year
   * @param quarters array of quarters in selected year
   */
  public static calculateSumOfQuarterValues(quarters: FormGroup<BmItemForm>[]): number {
    return quarters.reduce((acc: number, quarterFG: FormGroup<BmItemForm>): number =>
      acc + Number(quarterFG.getRawValue().itemValue || 0), 0);
  }

  /**
   * Calculate sum of all quarter values in all items array
   */
  public static recalculateRowTotal(items: FormArray<FormGroup<BmItemForm>>): number {
    return items.controls
      .filter((c) => c.value.period.type === BmPeriodEnum.QUARTER)
      .reduce(
        (acc, qCtrl) => acc + Number(qCtrl.getRawValue().itemValue ?? 0),
        0
      );
  }

  public static recalculateRowPcntTotal(totalRowValue: number, totalCapRequired: number): number {
    return totalCapRequired ? (totalRowValue / totalCapRequired) * 100 : 0;
  }


  /**
   * Set value and calcType in FormGroup, make it disabled.
   *
   * @param fg selected FormGroup
   * @param value value should be set
   * @param calcType eg. GENERATED/CALCULATED/MANUAL
   */
  public static patchAndDisable(fg: FormGroup<BmItemForm>, value: number, calcType: BmCalcType): void {
    fg.patchValue({
      itemValue: value,
      calcType: calcType,
    }, { emitEvent: false });
    fg.controls.itemValue.disable({ emitEvent: false });
  }


  public static createBmItemForm(item: BmItem): FormGroup<BmItemForm> {
    return new FormGroup({
      uuid: new FormControl<string>(item.uuid),
      period: new FormGroup<BmPeriodForm>({
        type: new FormControl(item.period.type),
        quarterNumber: new FormControl(item.period.quarterNumber),
        yearNumber: new FormControl(item.period.yearNumber),
      }),
      itemValue: new FormControl<number>(item.itemValue),
      calcType: new FormControl<BmCalcType>(item.calcType || BmCalcType.GENERATED),
    });
  }

  public static calculateAccumulatedValue(totalItems: FormArray<FormGroup<BmItemForm>>, targetYears: number, targetQuarters: number): number {
    if (!totalItems || !totalItems.length) {
      return 0;
    }

    let accumulated = 0;

    for (let i = 0; i < totalItems.length; i++) {
      const item = totalItems.at(i);
      if (!item) continue;

      const period = item.controls.period;
      const itemValue = item.controls.itemValue.value;
      const periodType = period?.controls.type.value;
      const quarterNumber = period?.controls.quarterNumber.value;
      const yearNumber = period?.controls.yearNumber.value;

      // Skip if no value or not quarterly
      if (!itemValue || periodType !== BmPeriodEnum.QUARTER || !quarterNumber || !yearNumber) {
        continue;
      }


      const isInRange = yearNumber < targetYears ||
        (yearNumber === targetYears && (targetQuarters === 0 || quarterNumber <= targetQuarters));

      if (isInRange) {
        accumulated += itemValue;
      }
    }

    return accumulated;
  }
}
